import { create } from 'zustand';
import type { Address, MemberEditableFields } from '@mlm/shared';

/**
 * In-memory auth state. Access token NEVER goes to localStorage/sessionStorage
 * (per CLAUDE.md). Refresh token lives in an httpOnly cookie the server sets
 * on login/refresh, never visible to JS.
 */
export type AuthUser = {
  id: string;
  email: string;
  // Read-only on the Profile tab; null when the tenant doesn't use usernames.
  username?: string | null;
  fullName: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  phone: string | null;
  // Read-only on the Profile tab; managed from the member's Address tab.
  address?: Address | null;
  // Member avatar — a presigned read URL from /me (null = show initials).
  profilePictureUrl?: string | null;
  status: string;
  kycStatus: string;
  /** `YYYY-MM-DD` or null (birthday gift checks, cashier phase 5). */
  birthDate?: string | null;
  roles: string[];
  perms: string[];
  totpEnabled: boolean;
  // QoL Slice 2 — when true, the SPA forces the user through a change-password
  // screen before any other route resolves. Cleared by the next /me/password.
  mustChangePassword?: boolean;
  referralCode: string | null;
  /**
   * Slot code the redeem flow would use as the sponsor when the member leaves
   * the form's sponsor field blank. Resolved server-side from
   * `users.sponsor_user_id` (set by replicated-link signup) → that user's
   * primary active slot, else super_admin. Null only on bootstrap failure.
   */
  defaultSponsorSlotCode?: string | null;
  branchId?: string | null;
  // QoL Slice 4 — present when the current session is an admin impersonating
  // this user. Drives the "Acting as X — Return to admin" banner.
  impersonator?: { userId: string; email: string } | null;
  // BF1: present on the /me payload; used by the re-acceptance gate.
  termsReacceptanceRequired?: boolean;
  tenantPolicy: {
    member2faRequired: boolean;
    requireTermsOnSignup?: boolean;
    currentTermsVersion?: number;
    // Which Profile-tab fields this member may edit (name / phone / photo).
    // Absent on older sessions — treat missing as editable.
    profileEditable?: MemberEditableFields;
  };
};

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  setMustChangePassword: (value: boolean) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  setSession: (token, user) => set({ token, user }),
  setMustChangePassword: (value) =>
    set((s) => (s.user ? { user: { ...s.user, mustChangePassword: value } } : s)),
  clear: () => set({ token: null, user: null }),
}));

export function hasPerm(perm: string): boolean {
  const { user } = useAuthStore.getState();
  if (!user) return false;
  if (user.roles.includes('super_admin')) return true;
  return user.perms.includes(perm);
}

export function hasRole(role: string): boolean {
  const user = useAuthStore.getState().user;
  return user ? user.roles.includes(role) : false;
}
